import { Box } from "@ui/components/layout/box/Box";
import { Text } from "@ui/components/content/text/Text";
import { focusOutline } from "@ui/css/02.focus/focus.styles";
import { scrollbarStyles } from "@ui/css/01.scrollbar/scrollbar.styles";
import { FilterBase } from "@components/commerce/product-listing/filters/common/filter/FilterBase";
import type { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import { Checkbox } from "@ui/components/forms/checkbox/Checkbox";
import type { ChangeEvent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { localCheckboxStyle } from "@components/commerce/product-listing/filters/toolbar/item/common/CommonToolbarFilterStyles";
import { SelectDropdownItem } from "@ui/components/overlay/select-dropdown/SelectDropdown";

export interface CommonToolbarFilterProps {
    initialFilters: Partial<RefinementListItem>[];
    filterName: string;
    items: RefinementListItem[];
    refine: (value: string) => void;
    isToolbar: boolean;
    isLoading?: boolean;
}

export const CommonToolbarFilter = ({
    initialFilters,
    items,
    refine,
    filterName,
    isToolbar,
    isLoading,
}: CommonToolbarFilterProps) => {
    // Local state to track optimistic checkbox states
    const [optimisticStates, setOptimisticStates] = useState<Record<string, boolean>>({});

    // Create lookup maps for performance optimization
    const itemsMap = useMemo(() => {
        const map = new Map<string, RefinementListItem>();
        items.forEach((item) => map.set(item.value, item));
        return map;
    }, [items]);

    const initialFiltersMap = useMemo(() => {
        const map = new Map<string, string>();
        initialFilters.forEach((filter) => {
            if (filter.label && filter.value) {
                map.set(filter.label, filter.value);
            }
        });
        return map;
    }, [initialFilters]);

    // Reset optimistic states when items change (after refine completes)
    // Use a more specific dependency to avoid unnecessary resets
    const itemsHash = useMemo(
        () => items.map((item) => `${item.value}:${item.isRefined}`).join("|"),
        [items]
    );

    useEffect(() => {
        setOptimisticStates({});
    }, [itemsHash]);

    // Handle checkbox selection with a single function
    const handleCheckboxSelection = useCallback(
        (event: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>) => {
            let value: string | undefined;

            // prevent click/change event from bubbling up to parent elements
            event.stopPropagation();

            // Handle mouse events (button clicks)
            if (event.type === "click" && event.target) {
                const target = event.target as HTMLButtonElement;
                value = target.getAttribute("data-itemLabel") as string | undefined;
            }
            // Handle change events (checkbox changes)
            else if (event.type === "change" && event.target) {
                const target = event.target as HTMLInputElement;
                value = target.value;
            }

            if (!value) {
                // eslint-disable-next-line no-console
                console.warn("No value found in event target");
                return;
            }

            const itemValue = initialFiltersMap.get(value);
            if (!itemValue) return;

            const dynamicItem = itemsMap.get(itemValue);
            const currentState = optimisticStates[itemValue] ?? dynamicItem?.isRefined ?? false;

            setOptimisticStates((prev) => ({
                ...prev,
                [itemValue]: !currentState,
            }));

            refine(itemValue);
        },
        [itemsMap, initialFiltersMap, optimisticStates, refine]
    );

    const dropdownItems = useMemo(
        () => (
            <Box
                sx={{
                    ...scrollbarStyles,
                    minWidth: ["auto", "210px"],
                    height: "auto",
                    maxHeight: "500px",
                    overflowY: "auto",
                    padding: 3,
                }}
            >
                {initialFilters?.map((item) => {
                    const dynamicItem = items.find((it) => it.value === item.value);

                    // Use optimistic state if available, otherwise use actual state
                    const isChecked =
                        optimisticStates[item.value] ?? dynamicItem?.isRefined ?? false;

                    return (
                        <SelectDropdownItem
                            as="div"
                            key={item.label}
                            sx={{
                                ...localCheckboxStyle,
                                _focusVisible: {
                                    ...focusOutline,
                                },
                                _focus: {
                                    background: "transparent",
                                },
                                _hover: { backgroundColor: "transparent", outline: "none" },
                            }}
                            data-itemLabel={item.label}
                            onClick={handleCheckboxSelection}
                            isDisabled={!dynamicItem}
                        >
                            <Checkbox
                                _focusVisible={{
                                    ...focusOutline,
                                }}
                                _hover={{
                                    outline: "none",
                                }}
                                id={item.label}
                                colorScheme={"black"}
                                value={item.label}
                                onChange={handleCheckboxSelection}
                                isChecked={isChecked}
                                isDisabled={!dynamicItem}
                                _disabled={{
                                    cursor: "default !important",
                                }}
                            >
                                <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                                    <Text noOfLines={1} fontWeight={"bold"}>
                                        {item.label}
                                    </Text>
                                    <Text color={"gray.700"} fontWeight={"bold"}>
                                        ({dynamicItem?.count ?? 0})
                                    </Text>
                                </Box>
                            </Checkbox>
                        </SelectDropdownItem>
                    );
                })}
            </Box>
        ),
        [initialFilters, itemsMap, optimisticStates, handleCheckboxSelection]
    );

    return (
        <FilterBase
            isLoading={isLoading}
            dropdownItems={dropdownItems}
            filterName={filterName}
            isToolbar={isToolbar}
        />
    );
};
